import { MenuItem, Select, useMediaQuery, useTheme } from '@material-ui/core'
import Paper from '@material-ui/core/Paper'
import { FormatBold, FormatItalic, FormatListBulleted, FormatListNumbered, FormatUnderlined, Redo, Undo } from '@material-ui/icons'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { convertFromRaw, DraftBlockType, EditorState, RawDraftContentState, RichUtils } from 'draft-js'
import React, { useEffect, useState } from 'react'
import RichTextEditor, { EditorValue } from 'react-rte'

import { Nullable } from 'models/Nullable'

export function useRichTextValue(defaultValue?: Nullable<RawDraftContentState>): [EditorValue, (value: EditorValue) => void] {
    const [editorValue, setEditorValue] = useState(() => RichTextEditor.createEmptyValue().setEditorState(defaultValue ? EditorState.createWithContent(convertFromRaw(defaultValue)) : EditorState.createEmpty()))

    const handleChange = (newValue: EditorValue) => {
        //console.log(newValue.getCurrentContent())
        setEditorValue(newValue)
    }

    // TODO: This is a workaround because the form renders before the data is fetched
    useEffect(() => {
        if (!defaultValue) {
            setEditorValue(RichTextEditor.createEmptyValue().setEditorState(EditorState.createEmpty()))
        } else {
            setEditorValue(RichTextEditor.createEmptyValue().setEditorState(EditorState.createWithContent(convertFromRaw(defaultValue))))
        }
    }, [defaultValue, setEditorValue])

    return [editorValue, handleChange]
}

type Props = {
    value: EditorValue
    onChange: (value: EditorValue) => void
}

function preventFocusLoss(e: React.MouseEvent<HTMLElement>) {
    e.preventDefault()
}

export function RichTextEditorWithToolbar({ value, onChange }: Props) {

    const theme = useTheme()

    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });

    return (
        <Paper elevation={3} style={{ padding: 20, borderRadius: 18, width: '100%' }}>
            <RichTextEditor
                value={value}
                onChange={onChange}
                placeholder="Informasjon i tilbud"
                rootStyle={{
                    backgroundColor: 'transparent',
                    border: 'none',
                }}
                toolbarStyle={{
                    border: 'none',
                    textAlign: 'center',
                }}
                toolbarConfig={{
                    display: [],
                    INLINE_STYLE_BUTTONS: [],
                    BLOCK_TYPE_DROPDOWN: [],
                    BLOCK_TYPE_BUTTONS: [],
                }}
                customControls={[
                    (set, get, state) => {
                        const styles = state.getCurrentInlineStyle().toArray()
                        const selectedBlockType = state
                            .getCurrentContent()
                            .getBlockForKey(state.getSelection().getStartKey())
                            .getType()

                        function toggleStyle(style: string) {
                            onChange(value.setEditorState(RichUtils.toggleInlineStyle(state, style)))
                        }

                        function toggleBlockType(blockType: DraftBlockType) {
                            onChange(value.setEditorState(RichUtils.toggleBlockType(state, blockType)))
                        }

                        function undo() {
                            onChange(value.setEditorState(EditorState.undo(state)))
                        }

                        function redo() {
                            onChange(value.setEditorState(EditorState.redo(state)))
                        }

                        const values = [...styles, selectedBlockType,]

                        if (state.getUndoStack().size !== 0) {
                            values.push('CAN-UNDO')
                        }

                        if (state.getRedoStack().size !== 0) {
                            values.push('CAN-REDO')
                        }


                        const options = [
                            "unstyled",
                            "header-one",
                            "header-two",
                            "header-three",
                        ]

                        const titles: any = {
                            "unstyled": 'Normal Tekst',
                            "header-one": 'Stor Overskrift',
                            "header-two": 'Medium Overskrift',
                            "header-three": 'Liten Overskrift',
                        }

                        const styleButtons = [
                            <ToggleButton key="BOLD" onClick={() => toggleStyle('BOLD')} onMouseDown={preventFocusLoss} value="BOLD" ><FormatBold /></ToggleButton>,
                            <ToggleButton key="ITALIC" onClick={() => toggleStyle('ITALIC')} onMouseDown={preventFocusLoss} value="ITALIC" ><FormatItalic /></ToggleButton>,
                            <ToggleButton key="UNDERLINE" onClick={() => toggleStyle('UNDERLINE')} onMouseDown={preventFocusLoss} value="UNDERLINE" ><FormatUnderlined /></ToggleButton>,
                        ]

                        const blockButtons = [
                            <ToggleButton key="unordered-list-item" onClick={() => toggleBlockType('unordered-list-item')} onMouseDown={preventFocusLoss} value="unordered-list-item"><FormatListBulleted /></ToggleButton>,
                            <ToggleButton key="ordered-list-item" onClick={() => toggleBlockType('ordered-list-item')} onMouseDown={preventFocusLoss} value="ordered-list-item"><FormatListNumbered /></ToggleButton>,
                        ]

                        const headerSelect = (
                            <div>
                                <Select
                                    variant="standard"
                                    labelId="demo-simple-select-outlined-label"
                                    id="demo-simple-select-outlined"
                                    value={options.includes(selectedBlockType) ? selectedBlockType : options[0]}
                                    onChange={e => toggleBlockType(e.target.value as DraftBlockType)}
                                    label="Tekst type"
                                    style={{ height: '100%', paddingLeft: 10 }}
                                >
                                    {options.map(option => (
                                        <MenuItem key={option} value={option}>{titles[option]}</MenuItem>
                                    ))}
                                </Select>
                            </div>
                        )

                        const historyButtons = [
                            <ToggleButton key="CAN-UNDO" onClick={() => undo()} onMouseDown={preventFocusLoss} value="CAN-UNDO"><Undo /></ToggleButton>,
                            <ToggleButton key="CAN-REDO" onClick={() => redo()} onMouseDown={preventFocusLoss} value="CAN-REDO"><Redo /></ToggleButton>,

                        ]

                        return (
                            <div key="custom-tolbar">
                                {isDesktop ? (
                                    <ToggleButtonGroup color="secondary" size="small" value={values}>
                                        {styleButtons}
                                        {blockButtons}
                                        {headerSelect}
                                        {historyButtons}
                                    </ToggleButtonGroup>
                                ) : (
                                    <>
                                        <ToggleButtonGroup color="secondary" size="small" value={values}>
                                            {styleButtons}
                                        </ToggleButtonGroup>
                                        <ToggleButtonGroup color="secondary" size="small" value={values}>
                                            {blockButtons}
                                        </ToggleButtonGroup>
                                        {headerSelect}
                                        <ToggleButtonGroup color="secondary" size="small" value={values}>
                                            {historyButtons}
                                        </ToggleButtonGroup>
                                    </>
                                )}
                            </div>
                        )
                    }
                ]}
            />
        </Paper>
    )
}